// frontend/src/components/book/atelier/coverPanelModel.js
//
// LOGIQUE PURE du panneau Couverture / 4e (sans React) : les styles proposes,
// ce que le moteur affiche reellement (lu dans l'apercu rendu), le style SUGGERE,
// les suggestions de date. Testable seule (coverPanelModel.test.js).
//
// Principe (maquette "Atelier Bookipix", panneau couverture, 2026-10-12) : plus de
// tuile « Automatique ». Un style est SUGGERE par defaut (pastille « Suggere ») ;
// apres un choix manuel, un lien « Revenir au style suggere » le restaure. Meme
// principe pour la couleur, les textes au-dessus du titre et la phrase de 4e.

// MIROIRS de backend/services/composition/{front,back}CoverRenderer.js
// (FRONT_COVER_VARIANTS / BACK_COVER_VARIANTS) : memes slugs, memes ordre que la
// maquette. Le moteur decide du rendu reel ; ceci ne sert qu'a l'affichage.
export const FRONT_STYLES = [
  { id: 'COVER_PHOTO_TITLE', shape: 'photo-full', hasPhoto: true, usesTone: false },
  { id: 'COVER_FRAMED', shape: 'framed', hasPhoto: true, usesTone: true },
  { id: 'COVER_PHOTO', shape: 'photo-band', hasPhoto: true, usesTone: true },
  { id: 'COVER_MULTI_PHOTO', shape: 'photo-trio', hasPhoto: true, usesTone: true },
  { id: 'COVER_MINIMAL', shape: 'text-only', usesTone: true },
  { id: 'COVER_COLLAB', shape: 'collab', hasPhoto: true, usesTone: true }
];

export const BACK_STYLES = [
  { id: 'BACK_MINIMAL', shape: 'back-minimal', usesTone: true },
  { id: 'BACK_PHOTO_STATS', shape: 'back-photo', hasPhoto: true, usesTone: false },
  { id: 'BACK_STRIP', shape: 'back-strip', hasPhoto: true, usesTone: true },
  { id: 'BACK_DARK', shape: 'back-dark', hasPhoto: true, usesTone: false },
  { id: 'BACK_CONTRIB', shape: 'back-contrib', usesTone: true }
];

// Choisir un style de couverture accorde la 4e (meme table que le moteur).
export const FRONT_TO_BACK_PAIR = {
  COVER_PHOTO_TITLE: 'BACK_DARK',
  COVER_FRAMED: 'BACK_PHOTO_STATS',
  COVER_PHOTO: 'BACK_MINIMAL',
  COVER_MULTI_PHOTO: 'BACK_STRIP',
  COVER_MINIMAL: 'BACK_MINIMAL',
  COVER_COLLAB: 'BACK_CONTRIB'
};

export const TONES = [
  { token: 'bleu', bg: '#3345e8', ink: '#ffffff' },
  { token: 'nuit', bg: '#1f2147', ink: '#eceefc' },
  { token: 'papier', bg: '#fffdf8', ink: '#2a2438' },
  { token: 'sauge', bg: '#dfe8dc', ink: '#25362b' },
  { token: 'rose', bg: '#f6dfe3', ink: '#4a2230' },
  { token: 'soleil', bg: '#ffcf3f', ink: '#2b2200' }
];

export const PHRASE_MAX = 140;

const stylesFor = (face) => (face === 'front' ? FRONT_STYLES : BACK_STYLES);
const overrideKey = (face) => (face === 'front' ? 'frontVariant' : 'backVariant');

/** Le style que le MOTEUR a reellement rendu, lu dans l'apercu (classe cvr-variant-XXX). */
export function readRenderedVariant(html) {
  const match = /cvr-variant-([A-Z_]+)/.exec(typeof html === 'string' ? html : '');
  return match ? match[1] : null;
}

/** Texte affiche par un element de l'apercu (ex. '.cvr-back-phrase'), ou ''. */
export function readRenderedText(html, selector) {
  if (typeof html !== 'string' || !html || typeof DOMParser === 'undefined') return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    return node ? (node.textContent || '').trim() : '';
  } catch (_error) {
    return '';
  }
}

/** Style AFFICHE comme choisi : le choix explicite, sinon ce que le moteur a rendu. */
export function selectedStyleId({ face, overrides, html }) {
  const list = stylesFor(face);
  const explicit = overrides && overrides[overrideKey(face)];
  if (list.some((style) => style.id === explicit)) return explicit;
  const rendered = readRenderedVariant(html);
  if (list.some((style) => style.id === rendered)) return rendered;
  return null;
}

/**
 * Style SUGGERE. Recto : ce que le moteur choisit tout seul (une couverture
 * typographique, ou une photo plein cadre des qu'une photo est posee). 4e : celle
 * qui s'accorde au recto actuel.
 */
export function suggestedStyleId({ face, overrides, frontStyleId }) {
  if (face === 'front') return overrides && overrides.frontPhotoId ? 'COVER_PHOTO_TITLE' : 'COVER_MINIMAL';
  return FRONT_TO_BACK_PAIR[frontStyleId] || 'BACK_MINIMAL';
}

/** Une teinte s'applique-t-elle au style courant ? (les styles « photo » n'en ont pas) */
export function toneApplies({ face, styleId }) {
  const style = stylesFor(face).find((entry) => entry.id === styleId);
  return !style || style.usesTone !== false;
}

/**
 * Valeur AFFICHEE dans un champ a trois etats (phrase de 4e, texte au-dessus du
 * titre) : texte personnalise, rien (champ vide = « aucun »), ou le texte
 * suggere par le moteur (lu dans l'apercu).
 */
export function fieldValue({ mode, text, autoText }) {
  if (mode === 'custom') return text || '';
  if (mode === 'none') return '';
  return autoText || '';
}

/** Ce qu'il faut enregistrer quand l'utilisateur modifie ce champ : vide = « aucun ». */
export function fieldPatch(value) {
  return String(value).trim() === '' ? { mode: 'none', text: '' } : { mode: 'custom', text: value };
}

const SEASON_BY_MONTH = ['winter', 'winter', 'spring', 'spring', 'spring', 'summer', 'summer', 'summer', 'autumn', 'autumn', 'autumn', 'winter'];

/**
 * Suggestions de date, de la plus probable a la plus simple. Partent de la date
 * de l'evenement du livre quand elle existe, sinon d'aujourd'hui. Chaque puce
 * tient dans les 20 caracteres que la couverture accepte.
 * `t(key)` donne le nom de la saison ; `locale` le nom du mois.
 */
export function dateSuggestions({ eventDate, now = new Date(), t, locale = 'fr' }) {
  const parsed = eventDate ? new Date(eventDate) : null;
  const date = parsed && !Number.isNaN(parsed.getTime()) ? parsed : now;
  const year = date.getFullYear();
  const season = t(`coverPanel.seasons.${SEASON_BY_MONTH[date.getMonth()]}`);
  let month = '';
  try {
    month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
    month = month.charAt(0).toUpperCase() + month.slice(1);
  } catch (_error) { /* locale inconnue : on se contente des deux autres */ }
  const all = [`${season} ${year}`, String(year), month ? `${month} ${year}` : ''];
  return all.filter((entry, index) => entry && entry.length <= 20 && all.indexOf(entry) === index);
}
